/**
 * Replaces `{key}` placeholders in a template string with values from an item object.
 * @param {string} template - Template string with `{key}` placeholders.
 * @param {Object} item - Data object used to fill placeholders.
 * @returns {string}
 */
export function fillTemplate(template, item) {
  return template.replace(/\{(\w+)\}/g, (_, key) => item?.[key] ?? '').trim();
}

/**
 * Cleans up a filled label template whose placeholders resolved to empty
 * strings. `'{applicantRefrenceId} - {firstName} {lastName} - ({lastdate})'`
 * against a candidate with no reference id used to render as
 * `'- Manik Bacha - ()'`; this collapses the orphaned separators and empty
 * brackets so it reads `'Manik Bacha'`.
 * @param {string} label
 * @returns {string}
 */
export function tidyTemplateLabel(label) {
  return String(label)
    .replace(/\(\s*\)/g, '')          // empty ( ) left by a blank placeholder
    .replace(/\s+/g, ' ')             // collapse the gaps those left behind
    .replace(/(^|\s)([-–—·|])(?=\s*\2)/g, '') // duplicated separators
    .replace(/^[\s\-–—·|]+/, '')      // leading separator
    .replace(/[\s\-–—·|]+$/, '')      // trailing separator
    .trim();
}

/**
 * Formats a date as a relative label: Today, Yesterday, or "N days ago".
 * @param {string|Date} value
 * @returns {string}
 */
export function relativeDateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
}

/**
 * Finds the most recent submission date on an item, checking common top-level
 * fields as well as `submissionHistory` entries.
 * @param {Object} item
 * @returns {string|null}
 */
function getLastSubmittedDate(item) {
  const history = Array.isArray(item?.submissionHistory) ? item.submissionHistory : [];
  const dates = [
    item?.lastSubmittedAt,
    item?.submittedAt,
    item?.updatedAt,
    ...history.map((entry) => entry?.submittedAt || entry?.updatedAt),
  ].filter(Boolean);

  if (!dates.length) return null;
  return dates.reduce((latest, curr) => (new Date(curr) > new Date(latest) ? curr : latest));
}

/**
 * The date an item was ACTUALLY submitted, for the already-submitted tick and
 * its tooltip. Deliberately stricter than getLastSubmittedDate above, which
 * falls back to `updatedAt` — a candidate that was merely edited yesterday and
 * never submitted must not report a submission date. Only fields that mean
 * "a submission happened" are consulted.
 * @param {Object} item
 * @returns {string|number|null}
 */
export function getSubmittedAt(item) {
  const history = Array.isArray(item?.submissionHistory) ? item.submissionHistory : [];
  const dates = [
    item?.lastSubmittedAt,
    item?.submittedAt,
    ...history.map((entry) => entry?.submittedAt),
  ].filter(Boolean);

  if (!dates.length) return null;
  return dates.reduce((latest, curr) => (new Date(curr) > new Date(latest) ? curr : latest));
}

/**
 * Normalizes a raw API item into a `{ label, value, _raw }` option object.
 * Supports plain strings, pre-normalized objects, and raw API response shapes.
 * @param {*} item
 * @param {Object} [field] - Field config with optional `labelKey`, `valueKey`, `labelTemplate`.
 * @returns {{ label: string, value: string, _raw?: * }}
 */
export function normalizeOption(item, field) {
  if (typeof item === 'string') return { label: item, value: item };
  if (item?.label !== undefined && item?.value !== undefined && !field?.labelTemplate && !field?.valueKey) return item;

  const { labelKey, valueKey, labelTemplate } = field ?? {};

  const templateItem =
    labelTemplate?.includes('{lastdate}')
      ? { ...item, lastdate: relativeDateLabel(getLastSubmittedDate(item)) }
      : item;

  const label =
    (labelTemplate ? tidyTemplateLabel(fillTemplate(labelTemplate, templateItem)) : null) ||
    (labelKey ? item?.[labelKey] : null) ||
    item?.name ||
    item?.fullName ||
    item?.candidateName ||
    item?.title ||
    item?.displayName ||
    item?.text ||
    item?.applicantReferenceId ||
    item?.applicantRefrenceId ||
    String(Object.values(item || {})[0] ?? '');

  const value =
    (valueKey ? item?.[valueKey] : null) ??
    item?.id ??
    item?._id ??
    item?.applicantReferenceId ??
    item?.applicantRefrenceId ??
    String(Object.values(item || {})[0] ?? '');

  // A fetcher (or the API itself — both /quicksubmit/list and /joblist carry a
  // `disable` + `message` pair) can mark a row unselectable. `disabled` has to
  // survive normalization for Ant Design to honour it per option, and
  // `optionNote` explains WHY: it is appended to the label (which a
  // labelTemplate would otherwise have already finalised) and used as the
  // hover title. Deliberately not named `title` — that key is a label fallback
  // above.
  const disabled = item?.disabled === true || item?.disable === true;
  const note = String(item?.optionNote ?? '').trim();
  const option = {
    label: note ? `${String(label)} — ${note}` : String(label),
    value: String(value),
    _raw: item,
  };
  if (disabled) option.disabled = true;
  if (note) option.title = note;

  // Lift the already-submitted marking to the TOP of the option. Options built
  // by hand (the job branch of quick-submit/api.js) already carry these keys;
  // hoisting them here means the dropdown renderer reads one shape for both,
  // instead of knowing which branch produced the row.
  //
  // Keyed on isSubmitted/alreadySubmitted ONLY — deliberately NOT on
  // isFromSubmission. Those mean different things: isSubmitted is "already
  // submitted to THIS job" (blocks), while isFromSubmission is "has a
  // submission to ANY job" (a prefill source, and the common case for a working
  // candidate). Treating isFromSubmission as a block here disabled every
  // candidate who had ever been submitted anywhere.
  if (item?.isSubmitted === true || item?.alreadySubmitted === true) {
    option.alreadySubmitted = true;
    option.existingSubmissionId = String(
      item?.existingSubmissionId ?? item?.jobSubmissionId ?? item?.submissionId ?? '',
    );
    // `submittedAt` is THIS job's submission date; getSubmittedAt is the
    // fallback for callers that only send a history. Preferring the explicit
    // field matters now that a row can also carry `lastSubmittedAt` from a
    // NEWER submission to a different job — the badge tooltip must report when
    // they were submitted to THIS job, not their latest activity anywhere.
    const submittedAt = item?.submittedAt || getSubmittedAt(item);
    if (submittedAt) option.submittedAt = submittedAt;
  }

  // The prior-submission prefill source, carried through untouched so the
  // popup's submit() can hand it to AddFormV1's prefillFromUrl.
  if (item?.isFromSubmission === true && item?.submissionId) {
    option.isFromSubmission = true;
    option.priorSubmissionId = String(item.submissionId);
    // When that prior submission happened, for the dropdown's history tick
    // tooltip. Deliberately NOT `submittedAt` — that one is pinned to THIS
    // job above, and on a row that is only submitted elsewhere it is absent.
    const priorAt = item?.lastSubmittedAt || getSubmittedAt(item);
    if (priorAt) option.priorSubmittedAt = priorAt;
  }
  return option;
}

/**
 * Resolves static options for a field from context or field defaults.
 * @param {Object} field
 * @param {Object} context
 * @returns {{ label: string, value: string }[]}
 */
export function resolveOptions(field, context) {
  const raw = context?.[field.optionsKey] ?? field.defaultOptions ?? [];
  return raw.map((opt) => normalizeOption(opt, field));
}

/**
 * Applies default values to a context object so consumers can rely on these keys always being present.
 * @param {Object} [context={}]
 * @returns {{ selectedIds: string[], selectedRecords: Object[], moduleName: string, removedIds: string[] }}
 */
export function getDefaultContext(context = {}) {
  return {
    selectedIds: [],
    selectedRecords: [],
    moduleName: '',
    removedIds: [],
    ...context,
  };
}
